import { Graphic, GraphicData, Label } from 'jszpl';
import { IMenuQrStepsCopy } from '@/domain/menu-qr/menu-qr-copy';
import { layoutMenuQr } from '@/domain/menu-qr/menu-qr-layout';
import { stickerSettingsToLayoutInput } from '@/domain/menu-qr/menu-qr-sticker';
import { IMenuQrStickerSettings } from '@/interfaces/IMenuQrStickerSettings';
import {
  createTitleMeasurer,
  drawMenuQrOverlay,
  ensureMenuQrFonts,
  getCanvasContext,
  loadOptionalImage,
  MENU_QR_FOOTER_SPRITE_PATH,
} from '@/lib/print/menu-qr-canvas';
import {
  MenuQrStickerSettings,
  normalizeMenuQrStickerSettings,
} from '@/lib/print/MenuQrStickerSettings';
import { createQrImage } from '@/utils';

const MENU_QR_STICKER_BW_THRESHOLD = 180;

export interface IMenuQrStickerPrintPayload {
  qrUrl: string;
  title: string;
  logoUrl?: string | null;
  settings?: IMenuQrStickerSettings;
  steps: IMenuQrStepsCopy;
}

function mmToDots(mm: number, dpmm: number) {
  return Math.max(1, Math.round(mm * dpmm));
}

function createCanvas(width: number, height: number) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
}

function rotateCanvas(
  sourceCanvas: HTMLCanvasElement,
  width: number,
  height: number,
  rotation: number
) {
  const finalCanvas = createCanvas(width, height);
  const context = getCanvasContext(finalCanvas);

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

  context.save();
  context.translate(finalCanvas.width / 2, finalCanvas.height / 2);
  context.rotate((rotation * Math.PI) / 180);
  context.drawImage(
    sourceCanvas,
    -sourceCanvas.width / 2,
    -sourceCanvas.height / 2
  );
  context.restore();

  return finalCanvas;
}

function canvasToImageBits(canvas: HTMLCanvasElement) {
  const context = getCanvasContext(canvas);
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const imageBits: number[] = [];

  for (let index = 0; index < imageData.data.length; index += 4) {
    const red = imageData.data[index];
    const green = imageData.data[index + 1];
    const blue = imageData.data[index + 2];
    const opacity = imageData.data[index + 3];

    let value = 0;

    if (opacity !== 0) {
      value = (red + green + blue) / 3 < MENU_QR_STICKER_BW_THRESHOLD ? 1 : 0;
    }

    imageBits.push(value);
  }

  return imageBits;
}

/**
 * Builds the ZPL for one QR sticker: the shared layout/drawing at the
 * printer's dpmm, rotated for the feed direction, thresholded to 1 bit.
 */
export class MenuQrStickerPrinter {
  static async buildZpl({
    qrUrl,
    title,
    logoUrl,
    settings,
    steps,
  }: IMenuQrStickerPrintPayload): Promise<string> {
    const normalizedSettings = normalizeMenuQrStickerSettings(
      settings ?? MenuQrStickerSettings.getDefaultSettings()
    );
    const {
      profile,
      pxPerMm,
      rotation,
      finalWidthMm,
      finalHeightMm,
    } = stickerSettingsToLayoutInput(normalizedSettings);

    await ensureMenuQrFonts();

    const [footerSprite, logoImage] = await Promise.all([
      loadOptionalImage(MENU_QR_FOOTER_SPRITE_PATH),
      loadOptionalImage(logoUrl),
    ]);

    const layoutCanvas = createCanvas(1, 1);
    const layoutContext = getCanvasContext(layoutCanvas);
    const layout = layoutMenuQr({
      profile,
      pxPerMm,
      hasLogo: !!logoImage,
      hasFooter: true,
      measureTitleWidth: createTitleMeasurer(layoutContext, title, 400),
    });

    layoutCanvas.width = layout.width;
    layoutCanvas.height = layout.height;
    layoutContext.fillStyle = '#ffffff';
    layoutContext.fillRect(0, 0, layout.width, layout.height);

    const qrImage = await createQrImage(qrUrl, layout.qr.width);

    drawMenuQrOverlay(layoutContext, layout, {
      qrImage,
      title,
      titleWeight: 400,
      logoImage,
      footerSprite,
      steps,
    });

    const finalCanvas = rotateCanvas(
      layoutCanvas,
      mmToDots(finalWidthMm, pxPerMm),
      mmToDots(finalHeightMm, pxPerMm),
      rotation
    );
    const label = new Label();
    const graphic = new Graphic();

    graphic.width = finalCanvas.width;
    graphic.height = finalCanvas.height;
    graphic.data = new GraphicData(
      finalCanvas.width,
      finalCanvas.height,
      canvasToImageBits(finalCanvas)
    );
    label.content.push(graphic);

    return label.generateZPL();
  }
}
